<template>
  <main class="error-page">
    <s-card
      shadow="medium"
      background-color="bg-primary"
      padding="medium"
      class="error-card"
    >
      <div class="error-content">
        <s-icon :icon="detail.icon" size="extra-large" :color="detail.color" />

        <h1 class="error-code">{{ statusCode }}</h1>

        <h2 class="error-title">{{ detail.title }}</h2>

        <p class="error-message">{{ detail.message }}</p>

        <div class="error-actions">
          <s-button
            icon="mdiHome"
            label="Voltar ao início"
            variant="tonal"
            color="primary"
            height="40px"
            @click="goHome"
          />

          <s-button
            icon="mdiRefresh"
            label="Tentar novamente"
            variant="outlined"
            color="gray"
            height="40px"
            @click="reload"
          />
        </div>
      </div>
    </s-card>

    <p class="text-copyright">
      Copyright © {{ year }} - <a href="https://quinyxcompany.com.br/" target="_blank">Quinyx</a>
    </p>
  </main>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app';
import { SCard, SButton, SIcon } from '@sieg-ad/componentes-vue';

const props = defineProps<{
  error: NuxtError;
}>();

useSeoMeta({ title: 'Erro' });

const statusCode = computed(() => props.error?.statusCode || 500);

interface ErrorDetail {
  icon: string;
  color: string;
  title: string;
  message: string;
}

// Mensagens amigáveis por status; mensagens do backend/`createError` entram como fallback.
const details: Record<number, ErrorDetail> = {
  400: { icon: 'mdiAlertCircleOutline', color: 'warning', title: 'Requisição inválida', message: 'Os dados enviados não puderam ser processados.' },
  401: { icon: 'mdiLockOutline', color: 'warning', title: 'Sessão expirada', message: 'Faça login novamente para continuar.' },
  403: { icon: 'mdiLockAlertOutline', color: 'warning', title: 'Acesso negado', message: 'Você não tem permissão para acessar esta página.' },
  404: { icon: 'mdiMapMarkerQuestionOutline', color: 'info', title: 'Página não encontrada', message: 'A página que você procura não existe ou foi movida.' },
  500: { icon: 'mdiServerNetworkOff', color: 'error', title: 'Erro interno', message: 'Algo deu errado do nosso lado. Tente novamente em instantes.' },
};

const detail = computed<ErrorDetail>(() => {
  const base = details[statusCode.value] ?? {
    icon: 'mdiAlertOctagonOutline',
    color: 'error',
    title: 'Ops! Algo deu errado',
    message: 'Ocorreu um erro inesperado.',
  };

  // Preserva a mensagem original do erro, quando disponível e não for o texto padrão do status.
  const original = props.error?.statusMessage || props.error?.message;
  return original ? { ...base, message: original } : base;
});

const year = new Date().getFullYear();

const goHome = () => clearError({ redirect: '/' });

const reload = () => {
  if (import.meta.client) window.location.reload();
};
</script>

<style scoped>
.error-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100vw;
  box-sizing: border-box;
  padding: 1rem;
}

.error-card {
  max-width: 480px;
  width: 100%;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  padding: 0.5rem;
}

.error-code {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  color: var(--primary);
}

.error-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text, inherit);
}

.error-message {
  margin: 0;
  color: var(--empty);
  max-width: 360px;
}

.error-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.text-copyright {
  text-align: center;
  margin: 10px;
  color: var(--empty);
}

.text-copyright a {
  color: var(--empty);
  text-decoration: none;
  transition: 0.3s ease;
}

.text-copyright a:hover {
  color: var(--secondary);
}
</style>
